/**
 * SmartFlowEditor - Editor principal de Smart Flows
 * Diseño: Light theme matching AdminCorrections pattern
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { publishNaraContext, clearNaraContext } from "../naraChatContext";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Switch,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  AutoAwesome as SmartIcon,
  Person as PersonIcon,
  Storage as StorageIcon,
  PlayArrow as SimulatorIcon,
  Save as SaveIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  Check as CheckIcon,
  WhatsApp as WhatsAppIcon,
  Circle as DotIcon,
} from "@mui/icons-material";

import { StyledDialog } from "../common";

import PersonalityTab from "./tabs/PersonalityTab";
import KnowledgeBaseTab from "./tabs/KnowledgeBaseTab";
import SimulatorTab from "./tabs/SimulatorTab";

const API_URL = process.env.REACT_APP_API_URL || "https://turismo.nara.chat/api";

// Background pattern (light theme)
const bgPattern = `
  radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.06) 0%, transparent 50%),
  radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.04) 0%, transparent 40%),
  radial-gradient(circle at 50% 80%, rgba(245, 158, 11, 0.03) 0%, transparent 40%)
`;

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`smartflow-tabpanel-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function SmartFlowEditor() {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [activeTab, setActiveTab] = useState(0);

  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [twilioNumbers, setTwilioNumbers] = useState([]);
  const [newNumber, setNewNumber] = useState("");
  const [savingNumber, setSavingNumber] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/smart-flows/${chatbotId}`);
      if (!response.ok) throw new Error("Error cargando configuración");
      const data = await response.json();
      setConfig(data);
      // Publish chatbot context to NaraChat
      publishNaraContext('chatbot', {
        id: data.chatbot_id || chatbotId,
        nombre: data.chatbot_name || data.name,
        whatsapp: data.whatsapp_number,
        tipo: data.flow_type,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    fetchConfig();
    return () => clearNaraContext();
  }, [fetchConfig]);

  const saveAgentConfig = async (updatedConfig) => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/smart-flows/${chatbotId}/agent-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedConfig),
      });
      if (!response.ok) throw new Error("Error guardando configuración");
      const data = await response.json();
      setConfig((prev) => ({ ...prev, agent_config: data.agent_config }));
      setHasChanges(false);
      setSnackbar({ open: true, message: "Guardado", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const toggleAgentEnabled = async () => {
    await saveAgentConfig({ enabled: !config.agent_config.enabled });
  };

  const updateSystemPrompt = (newPrompt) => {
    setConfig((prev) => ({
      ...prev,
      agent_config: { ...prev.agent_config, system_prompt: newPrompt },
    }));
    setHasChanges(true);
  };

  const refreshKnowledgeBase = () => fetchConfig();

  const fetchTwilioNumbers = async () => {
    try {
      const response = await fetch(`${API_URL}/smart-flows/twilio-numbers/all`);
      if (response.ok) setTwilioNumbers(await response.json());
    } catch (err) {
      console.error("Error cargando números Twilio:", err);
    }
  };

  const openWhatsappDialog = () => {
    fetchTwilioNumbers();
    setNewNumber("");
    setWhatsappDialogOpen(true);
  };

  const assignWhatsappNumber = async (numeroTwilio) => {
    try {
      setSavingNumber(true);
      const response = await fetch(`${API_URL}/smart-flows/${chatbotId}/assign-number`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ numero_twilio: numeroTwilio }),
      });
      if (!response.ok) throw new Error("Error asignando número");
      const result = await response.json();
      setSnackbar({ open: true, message: result.message, severity: "success" });
      setWhatsappDialogOpen(false);
      fetchConfig();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setSavingNumber(false);
    }
  };

  const unassignWhatsappNumber = async () => {
    try {
      setSavingNumber(true);
      const response = await fetch(`${API_URL}/smart-flows/${chatbotId}/unassign-number`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Error desasignando número");
      setSnackbar({ open: true, message: "Número desasignado", severity: "success" });
      setWhatsappDialogOpen(false);
      fetchConfig();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setSavingNumber(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f1f5f9",
          backgroundImage: bgPattern,
        }}
      >
        <CircularProgress sx={{ color: "#6366f1" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: "100vh", p: 3, bgcolor: "#f1f5f9", backgroundImage: bgPattern }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate("/smart-flows")} sx={{ color: "#1e293b" }}>
          Volver
        </Button>
      </Box>
    );
  }

  const isEnabled = config.agent_config?.enabled;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f1f5f9", backgroundImage: bgPattern }}>
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
        }}
      >
        {/* Left: Back + Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={() => navigate("/smart-flows")}
            sx={{
              color: "#64748b",
              p: 0.5,
              "&:hover": { color: "#1e293b", bgcolor: "#f1f5f9" },
            }}
          >
            <BackIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              background: "linear-gradient(135deg, #a855f7, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(168, 85, 247, 0.4)",
              "& svg": { fontSize: 20 },
            }}
          >
            <SmartIcon />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  color: "#1e293b",
                  fontWeight: 600,
                  fontSize: 15,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: 150, sm: 250, md: 350 },
                }}
              >
                {config.chatbot_name}
              </Typography>
              <DotIcon
                sx={{
                  fontSize: 8,
                  color: isEnabled ? "#22c55e" : "#cbd5e1",
                }}
              />
            </Box>

            {/* WhatsApp clickable */}
            <Tooltip title="Configurar WhatsApp">
              <Box
                onClick={openWhatsappDialog}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                {config.whatsapp_number ? (
                  <>
                    <WhatsAppIcon sx={{ fontSize: 12, color: "#22c55e" }} />
                    <Typography sx={{ color: "#94a3b8", fontSize: 11 }}>
                      {config.whatsapp_number}
                    </Typography>
                  </>
                ) : (
                  <>
                    <AddIcon sx={{ fontSize: 12, color: "#f59e0b" }} />
                    <Typography sx={{ color: "#f59e0b", fontSize: 11, fontWeight: 500 }}>
                      Configurar WhatsApp
                    </Typography>
                  </>
                )}
              </Box>
            </Tooltip>
          </Box>
        </Box>

        {/* Right: Status + Save */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Status toggle */}
          <Box
            onClick={toggleAgentEnabled}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              cursor: "pointer",
              bgcolor: isEnabled ? "#f0fdf4" : "#f8fafc",
              border: isEnabled ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
              transition: "all 0.15s",
              "&:hover": {
                bgcolor: isEnabled ? "#dcfce7" : "#f1f5f9",
              },
            }}
          >
            <Switch
              checked={isEnabled}
              size="small"
              sx={{
                width: 32,
                height: 18,
                p: 0,
                "& .MuiSwitch-switchBase": {
                  p: 0.25,
                  "&.Mui-checked": {
                    transform: "translateX(14px)",
                    color: "#22c55e",
                    "& + .MuiSwitch-track": { bgcolor: "#22c55e", opacity: 0.5 },
                  },
                },
                "& .MuiSwitch-thumb": { width: 14, height: 14 },
                "& .MuiSwitch-track": { borderRadius: 9, bgcolor: "#cbd5e1" },
              }}
            />
            <Typography sx={{ color: isEnabled ? "#16a34a" : "#94a3b8", fontSize: 12, fontWeight: 600 }}>
              {isEnabled ? "Activo" : "Inactivo"}
            </Typography>
          </Box>

          {/* Save button */}
          {hasChanges && (
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={() => saveAgentConfig({ system_prompt: config.agent_config.system_prompt })}
              disabled={saving}
              sx={{
                background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                boxShadow: "0 4px 14px rgba(34, 197, 94, 0.4)",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 600,
                py: 0.5,
                px: 1.5,
                borderRadius: 2,
                "&:hover": {
                  background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                  boxShadow: "0 6px 20px rgba(34, 197, 94, 0.5)",
                },
              }}
            >
              Guardar
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          borderBottom: "1px solid #e2e8f0",
          background: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              color: "#94a3b8",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              minHeight: 42,
              py: 1,
              px: 2,
              borderRadius: "8px 8px 0 0",
              transition: "all 0.2s ease",
              "&:hover": { bgcolor: "rgba(0,0,0,0.02)", color: "#64748b" },
              "&.Mui-selected": {
                color: "#1e293b",
                fontWeight: 600,
              },
            },
            "& .MuiTabs-indicator": {
              height: 2,
              background: "linear-gradient(90deg, #a855f7, #6366f1)",
              boxShadow: "0 0 8px rgba(168, 85, 247, 0.4)",
              borderRadius: 1,
            },
          }}
        >
          <Tab icon={<PersonIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Personalidad" />
          <Tab
            icon={<StorageIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                Base de Conocimiento
                <Chip
                  label={config.knowledge_entries?.length || 0}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: "#dbeafe",
                    color: "#2563eb",
                    border: "1px solid #bfdbfe",
                  }}
                />
              </Box>
            }
          />
          <Tab icon={<SimulatorIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Simulador" />
        </Tabs>
      </Box>

      {/* Content area */}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <TabPanel value={activeTab} index={0}>
          <PersonalityTab
            config={config.agent_config}
            chatbotName={config.chatbot_name}
            onUpdate={updateSystemPrompt}
            onSave={(identityData) => saveAgentConfig({
              system_prompt: config.agent_config.system_prompt,
              ...identityData,
            })}
            saving={saving}
            hasChanges={hasChanges}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <KnowledgeBaseTab
            chatbotId={chatbotId}
            entries={config.knowledge_entries || []}
            categories={config.kb_categories || []}
            onRefresh={refreshKnowledgeBase}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <SimulatorTab
            chatbotId={chatbotId}
            chatbotName={config.chatbot_name}
            agentConfig={config.agent_config}
          />
        </TabPanel>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontSize: 13 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* WhatsApp Dialog → StyledDialog */}
      <StyledDialog
        open={whatsappDialogOpen}
        onClose={() => setWhatsappDialogOpen(false)}
        title="Configurar WhatsApp"
        icon={<WhatsAppIcon />}
        variant="compact"
        maxWidth="sm"
      >
        <StyledDialog.Body compact>
          {config?.whatsapp_number && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: "rgba(34, 197, 94, 0.08)",
                border: "1px solid rgba(34, 197, 94, 0.2)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                  Número actual:
                </Typography>
                <Typography sx={{ fontWeight: 600, color: "#16a34a", fontSize: 14 }}>
                  {config.whatsapp_number}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                onClick={unassignWhatsappNumber}
                disabled={savingNumber}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Desasignar
              </Button>
            </Box>
          )}

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "#1e3a5f" }}>
              Números disponibles:
            </Typography>

            {twilioNumbers.length === 0 ? (
              <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>
                No hay números configurados.
              </Typography>
            ) : (
              <List sx={{ py: 0 }}>
                {twilioNumbers.map((num) => (
                  <ListItem key={num.id} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => assignWhatsappNumber(num.numero_twilio)}
                      disabled={savingNumber}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        bgcolor:
                          num.chatbot_id === parseInt(chatbotId)
                            ? "rgba(34, 197, 94, 0.08)"
                            : "rgba(0,0,0,0.02)",
                        border:
                          num.chatbot_id === parseInt(chatbotId)
                            ? "1px solid rgba(34, 197, 94, 0.2)"
                            : "1px solid rgba(0,0,0,0.06)",
                        "&:hover": { bgcolor: "rgba(30, 58, 95, 0.06)" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <PhoneIcon
                          sx={{
                            fontSize: 18,
                            color: num.chatbot_id === parseInt(chatbotId) ? "#22c55e" : "#94a3b8",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={num.numero_display || num.numero_twilio}
                        secondary={
                          num.chatbot_id
                            ? num.chatbot_id === parseInt(chatbotId)
                              ? "Asignado aquí"
                              : `Usado por: ${num.chatbot_name}`
                            : "Disponible"
                        }
                        primaryTypographyProps={{ color: "#1e293b", fontSize: 13, fontWeight: 500 }}
                        secondaryTypographyProps={{
                          color: num.chatbot_id ? "#f59e0b" : "#22c55e",
                          fontSize: 11,
                        }}
                      />
                      {num.chatbot_id === parseInt(chatbotId) && <CheckIcon sx={{ color: "#22c55e", fontSize: 18 }} />}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "#1e3a5f" }}>
              Agregar número:
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                placeholder="whatsapp:+573001234567"
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "#fff",
                    fontSize: 13,
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
                  },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => assignWhatsappNumber(newNumber)}
                disabled={!newNumber || savingNumber}
                sx={{
                  bgcolor: "#1e3a5f",
                  "&:hover": { bgcolor: "#2d5a87" },
                  minWidth: 80,
                  fontSize: 12,
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                {savingNumber ? <CircularProgress size={16} color="inherit" /> : "Asignar"}
              </Button>
            </Box>
          </Box>
        </StyledDialog.Body>

        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setWhatsappDialogOpen(false)}>
            Cerrar
          </StyledDialog.CancelButton>
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}
